import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { Button, Flex, Image } from '@zillow/constellation';

import stringHelper from 'app/shared/utils/stringHelper';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import ClaimActions from 'app/shared/actions/ClaimActions';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import { STEP } from 'app/shared/containers/ListingClaimPage/constants';

import WizardBodyTemplate from 'app/shared/components/WizardBodyTemplate';
import WizardActionFooter from 'app/shared/components/WizardActionFooter';
import ViewClaimListingsModal from 'app/shared/components/Modal/ViewClaimListingsModal';
import PersonProfile from 'app/shared/images/PersonProfile.svg';
import HomeSearch from 'app/shared/images/HomeSearch.svg';

import { StyledAnchor } from './StyledComponents';

const { pluralize } = stringHelper;

const propTypes = {
    onGoToPrevStep: PropTypes.func.isRequired,
    onGoToNextStep: PropTypes.func.isRequired,
    onGoToStep: PropTypes.func.isRequired,
    progressStepper: PropTypes.node.isRequired,
};

const defaultProps = {};

const WizVerifyContactSuccess = ({
    onGoToNextStep,
    onGoToPrevStep,
    onGoToStep,
    progressStepper,
}) => {
    const dispatch = useDispatch();
    const [isTransitioning, setIsTransitioning] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [listingCount, setListingCount] = useState('');
    const [contactMethodsCount, setContactMethodsCount] = useState('');
    const [showViewListingsModal, setShowViewListingsModal] = useState(false);

    const handleClickPrevStep = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK_BACK,
            { label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.CONTACT_VERIFICATION_SUCCESSFUL },
        ));
        onGoToPrevStep();
    };

    const handleClickNextStep = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK_NEXT,
            { label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.CONTACT_VERIFICATION_SUCCESSFUL },
        ));
        onGoToNextStep();
    };

    const handleViewListings = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK,
            {
                label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.CONTACT_VERIFICATION_SUCCESSFUL,
                action: 'ViewListings',
            },
        ));
        setShowViewListingsModal(true);
    };

    useEffect(() => {
        setIsTransitioning(true);
        dispatch(ClaimActions.propertyLookupAll({ includeUnverified: false }))
            .then((response) => {
                const {
                    totalContactMethodCount, totalListingCount,
                } = response;
                setListingCount(totalListingCount);
                setContactMethodsCount(totalContactMethodCount);
            }).catch(() => setHasError(true))
            .finally(() => setIsTransitioning(false));
    }, []);

    const handleFindListings = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK,
            {
                label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.CONTACT_VERIFICATION_SUCCESSFUL,
                action: 'FindListings',
            },
        ));
        dispatch(ClaimActions.setShowPropertyLookupModal(true));
        onGoToStep(STEP.FIND_LISTING_RESULTS);
    };

    const renderSubtitle = () => (listingCount ? (
        <>
            {`Congratulations! You have verified ${pluralize(contactMethodsCount, 'contact', 'contacts')} and we've 
            linked ${pluralize(listingCount, 'listing', 'listings')} to your account. `}
            <StyledAnchor onClick={handleViewListings}>View your listings</StyledAnchor>
        </>
    ) : (
        <>
            {`No listings are associated with the contact you verified.
             To transfer listings, search by other contact methods.`}
        </>
    ));

    const renderDescription = () => (
        listingCount ? (
            <>
                We&apos;ll continue searching for listings with this contact information and alert you with new verified
                listings in your Zillow Rental Manager dashboard.
            </>
        ) : null
    );

    const renderIcon = () => <Image src={listingCount ? PersonProfile : HomeSearch} alt="" />;

    return (
        <WizardBodyTemplate
            title={`You've claimed ${pluralize(listingCount, 'listing', 'listings')}`}
            subtitle={renderSubtitle()}
            description={renderDescription()}
            isTransitioning={isTransitioning}
            hasError={hasError}
            icon={renderIcon()}
            progressStepper={progressStepper}
            footer={(
                <WizardActionFooter
                    onBackClick={handleClickPrevStep}
                    onNextClick={handleClickNextStep}
                    isBackDisabled={!onGoToPrevStep}
                    isNextDisabled={listingCount < 1}
                />
            )}
        >
            {!listingCount && (
                <Flex display="flex" flexDirection="column" alignItems="center">
                    <Button buttonType="primary" onClick={handleFindListings} marginTop="xs">Find Listings</Button>
                </Flex>
            )}
            {showViewListingsModal && (
                <ViewClaimListingsModal
                    onClose={() => setShowViewListingsModal(false)}
                />
            )}
        </WizardBodyTemplate>
    );
};

WizVerifyContactSuccess.propTypes = propTypes;
WizVerifyContactSuccess.defaultProps = defaultProps;

export default WizVerifyContactSuccess;
